/**
 * Los gestos de planificar, en un sitio: qué eventos escribe cada uno.
 *
 * Cada escritura guarda exactamente el evento que se le pide; cuando un gesto
 * toca varios (quitar un plato que dos móviles pusieron a la vez), los pide
 * uno a uno y lo dice aquí.
 */
import type { Propuesta } from '@shared/dietas'
import type { Course, HouseholdEvent, MealSlot } from '@shared/events'
import { planEventsAt } from '@shared/plate'
import type { DayKey } from '@shared/time'
import type { Eleccion } from '../components/ElegirReceta'
import { deleteEvent, newEvent, saveEvent } from './sync'

/**
 * Poner un plato en su sitio. Si ya había uno, se edita ese mismo evento en
 * vez de apilar otro encima: la tabla de revisiones guarda el anterior.
 * Devuelve cómo deshacerlo: devolver el plato de antes o quitar el nuevo.
 */
export async function planificar(
  events: readonly HouseholdEvent[],
  userId: string,
  dayKey: DayKey,
  slot: MealSlot,
  course: Course,
  eleccion: Eleccion,
): Promise<() => Promise<void>> {
  const actual = planEventsAt(events, dayKey, slot, course).sort((a, b) =>
    b.updatedAt.localeCompare(a.updatedAt),
  )[0]
  const payload = { dayKey, slot, course, ...eleccion }
  if (actual) {
    await saveEvent({ ...actual, payload })
    return () => saveEvent(actual)
  }
  const nuevo = newEvent({ type: 'meal_plan', createdBy: userId, payload })
  await saveEvent(nuevo)
  return () => deleteEvent(nuevo.id)
}

/** Quita un plato y devuelve cómo deshacerlo. */
export async function quitarPlato(
  events: readonly HouseholdEvent[],
  dayKey: DayKey,
  slot: MealSlot,
  course: Course,
): Promise<() => Promise<void>> {
  const quitados = planEventsAt(events, dayKey, slot, course)
  for (const event of quitados) await deleteEvent(event.id)
  return async () => {
    for (const event of quitados) await saveEvent({ ...event, deletedAt: null })
  }
}

/**
 * Pone lo que propone la generación de la semana: un evento por plato, como
 * si se hubieran elegido a mano. Devuelve cómo quitarlos todos de golpe.
 */
export async function ponerPropuestas(
  propuestas: readonly Propuesta[],
  userId: string,
): Promise<() => Promise<void>> {
  const ids: string[] = []
  for (const propuesta of propuestas) {
    const event = newEvent({ type: 'meal_plan', createdBy: userId, payload: { ...propuesta } })
    await saveEvent(event)
    ids.push(event.id)
  }
  return async () => {
    for (const id of ids) await deleteEvent(id)
  }
}
